import { Card, CardContent } from '@/components/ui/card';
import {
  asArray,
  formatCad,
  optionPriceLabel,
  optionSections,
  productPresentation,
  productPricing,
} from '@/lib/documentServices';

/**
 * The numbered sections a buyer works through, and the summary they read
 * before paying.
 *
 * Both are driven entirely by the server payload — the section order, the
 * wording, and what each option adds all come from the product record. Nothing
 * here hard-codes "delivery method" or "processing speed", so staff can
 * reorder or rename the sections from the product editor and this follows.
 *
 * The one design rule worth stating: an option shows what it *adds*, not what
 * the whole combination costs. Two lists of absolute prices sitting side by
 * side read as though they stack, which is exactly how a $45.00 order came to
 * look like $75.00.
 */

function SectionHeading({ index, title, subtitle }) {
  return (
    <div className="mb-4">
      <h3 className="flex items-baseline gap-2 text-lg font-semibold text-[#062B1A]">
        <span className="text-sm font-semibold tabular-nums text-[#D4AF37]">
          {String(index).padStart(2, '0')}
        </span>
        {title}
      </h3>
      {subtitle && <p className="mt-1 text-sm text-[#062B1A]/55">{subtitle}</p>}
    </div>
  );
}

function OptionCard({ value, active, displayMode, onSelect }) {
  const priceLabel = optionPriceLabel(value, displayMode);
  const included = displayMode !== 'absolute' && value.uplift_cents === 0;
  return (
    <button
      type="button"
      onClick={() => onSelect(value.value)}
      disabled={value.available === false}
      aria-pressed={active}
      className={`rounded-xl border p-4 text-left transition disabled:cursor-not-allowed disabled:opacity-45 ${
        active
          ? 'border-[#D4AF37] bg-[#D4AF37]/10 ring-2 ring-[#D4AF37]/15'
          : 'border-[#062B1A]/10 bg-white hover:border-[#062B1A]/25'
      }`}
    >
      <span className="block font-semibold text-[#062B1A]">{value.label}</span>
      {value.description && (
        <span className="mt-0.5 block text-xs text-[#062B1A]/50">{value.description}</span>
      )}
      <span
        className={`mt-1 block text-sm font-medium ${
          included ? 'text-[#062B1A]/45' : 'text-[#062B1A]'
        }`}
      >
        {value.available === false ? 'Not available' : priceLabel}
      </span>
    </button>
  );
}

function QuantityStepper({ value, max, label, onChange }) {
  const clamp = (next) => Math.min(max, Math.max(1, next));
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#062B1A]/10 p-4">
      <div>
        <p className="font-medium text-[#062B1A]">{label}</p>
        <p className="mt-0.5 text-xs text-[#062B1A]/50">
          Up to {max}. Delivery and processing are charged once for the order.
        </p>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(clamp(value - 1))}
          disabled={value <= 1}
          aria-label="One fewer copy"
          className="size-9 rounded-lg border border-[#062B1A]/15 text-lg font-medium text-[#062B1A] disabled:opacity-40"
        >
          −
        </button>
        <span
          aria-live="polite"
          className="w-10 text-center text-base font-semibold tabular-nums text-[#062B1A]"
        >
          {value}
        </span>
        <button
          type="button"
          onClick={() => onChange(clamp(value + 1))}
          disabled={value >= max}
          aria-label="One more copy"
          className="size-9 rounded-lg border border-[#062B1A]/15 text-lg font-medium text-[#062B1A] disabled:opacity-40"
        >
          +
        </button>
      </div>
    </div>
  );
}

export function OrderOptions({ product, selection, quantity, onSelect, onQuantityChange }) {
  const sections = optionSections(product);
  const pricing = productPricing(product);
  const presentation = productPresentation(product);
  const quantityAllowed = Boolean(product?.quantity_allowed) && Number(product?.max_quantity || 1) > 1;
  // The quantity section is always first: a buyer decides what they want before
  // deciding how fast it moves or how it travels.
  let step = 0;

  return (
    <div className="space-y-5">
      {quantityAllowed && (
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="p-5">
            <SectionHeading
              index={(step += 1)}
              title={presentation.quantity_section_label}
              subtitle={
                pricing.additional_copy_price_label
                  ? `Each additional copy is ${pricing.additional_copy_price_label}.`
                  : ''
              }
            />
            <QuantityStepper
              value={quantity}
              max={Number(product?.max_quantity || 1)}
              label={presentation.quantity_field_label}
              onChange={onQuantityChange}
            />
          </CardContent>
        </Card>
      )}

      {sections.map((section) => (
        <Card key={section.id} className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="p-5">
            <SectionHeading index={(step += 1)} title={section.label} subtitle={section.description} />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {asArray(section.values).map((value) => (
                <OptionCard
                  key={value.value}
                  value={value}
                  displayMode={pricing.display_mode}
                  active={selection?.[section.id] === value.value}
                  onSelect={(next) => onSelect(section.id, next)}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {!sections.length && (
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="p-5 text-sm text-[#062B1A]/55">
            No purchasable options are currently available.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * The order summary.
 *
 * Every line names the choice and what it added — "Courier Service (delivery
 * method) +$25.00" — rather than pairing a category with a value and no money,
 * which is what left buyers guessing at the total.
 */
export function OrderDetails({ product, breakdown, footer }) {
  const presentation = productPresentation(product);
  const lines = asArray(breakdown?.lines);
  const total = Number(breakdown?.total_cents || 0);

  return (
    <Card className="border border-[#D4AF37]/30 shadow-sm">
      <CardContent className="space-y-4 p-5">
        <div className="flex items-baseline justify-between gap-3">
          <h2
            className="text-base font-semibold text-[#062B1A]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {presentation.summary_heading}
          </h2>
          {/* Staff can switch the catalog code back on; buyers never need it. */}
          {presentation.show_product_code && product?.product_id && (
            <span className="text-xs tabular-nums text-[#062B1A]/40">{product.product_id}</span>
          )}
        </div>

        <div className="space-y-2.5">
          {lines.map((line, index) => (
            <div key={`${line.kind}-${line.dimension_id || index}`} className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm text-[#062B1A]">{line.label}</p>
                {line.detail && <p className="mt-0.5 text-xs text-[#062B1A]/45">{line.detail}</p>}
              </div>
              <span
                className={`shrink-0 text-sm tabular-nums ${
                  line.amount_cents === 0
                    ? 'text-[#062B1A]/45'
                    : 'font-medium text-[#062B1A]'
                }`}
              >
                {line.amount_label}
              </span>
            </div>
          ))}
          {!lines.length && (
            <p className="text-sm text-[#062B1A]/50">Choose your options to see the total.</p>
          )}
        </div>

        {presentation.show_tax_note && presentation.tax_note && (
          <p className="text-xs text-[#062B1A]/45">{presentation.tax_note}</p>
        )}

        <div className="flex items-baseline justify-between border-t border-[#062B1A]/10 pt-3">
          <span className="font-semibold text-[#062B1A]">Total CAD</span>
          <span className="text-2xl font-bold tabular-nums text-[#062B1A]">{formatCad(total)}</span>
        </div>

        {footer}
      </CardContent>
    </Card>
  );
}

export default OrderOptions;
